import { useMutation } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ForgotPasswordCommand,
  ForgotPasswordResponse,
  ResetPasswordCommand,
  ResetPasswordResponse,
  MfaSetupResult,
  MfaSetupStartResult,
  MfaSetupVerifyModel,
  siginResponseModel,
  signInCredsModel,
  VerifyTotpModel,
  VerifyTwoFactorModel,
} from "./types";
import queryClient from "../../../common/query-client";
import useHomeStore from "../../home/api/store";
import { useSignInStore } from "./stores";
import { usePost } from "../../../common/api-service";
import useMainStore from "../../landing-page/api/store";
import { enqueueSnackbar } from "notistack";
import { useAuthStore } from "../../../common/auth/store";
import { AuthState } from "../../../common/auth/types";
import { Handlers } from "../../landing-page/api/handlers";

const endpoint = "/api/user";

export const useSignIn = ({ onError }: { onError: (error: any) => void }) => {
  const location = useLocation();
  const { post } = usePost();
  const { setAuthState } = useAuthStore();

  const { rememberMe } = useSignInStore();
  const navigate = useNavigate();
  return useMutation<siginResponseModel, Error, signInCredsModel>({
    mutationFn: (data: signInCredsModel) => {
      try {
        // removeAuthorizationHeader();
        return post<signInCredsModel, siginResponseModel>(
          `${endpoint}/login`,
          data,
          false,
        );
      } catch (error: any) {
        if (error?.response?.data) {
          throw new Error(error.response.data);
        }
        throw new Error("An unexpected error occurred");
      }
    },
    onSuccess: async (newData: siginResponseModel, variables) => {
      if (newData.requiresConfirmation) {
        const {
          setFacilityDisengagedDialogOpen,
          setDisengagedProductNames,
          setPendingSignInPayload,
        } = useSignInStore.getState();

        setDisengagedProductNames(newData.disengagedProductNames ?? []);
        setPendingSignInPayload(variables);
        setFacilityDisengagedDialogOpen(true);

        return;
      }

      if (newData.requiresTwoFactor) {
        const params = new URLSearchParams(window.location.search);
        const callback = params.get("callback");
        const origin = params.get("origin") || "/";
        if (!callback) {
          navigate("/otp", {
            state: {
              email: variables.email,
              remember: variables.remember,
              isNavigatingInsideApplication: true,
            },
          });
          return;
        } else {
          const otpUrl =
            `/otp` +
            `?callback=${encodeURIComponent(callback)}` +
            `&origin=${encodeURIComponent(origin)}`;
          navigate(otpUrl, {
            replace: true,
            state: {
              email: variables.email,
              remember: variables.remember,
              isNavigatingInsideApplication: true,
            },
          });
          return;
        }
      }

      // Authenticator (TOTP) step-up — works for both the direct app login and the SSO/callback
      // flow (aud + callback preserved so the product token is minted after MFA). feature/totp-mfa.
      if (newData.requiresTotp) {
        const params = new URLSearchParams(window.location.search);
        const callback = params.get("callback");
        const aud = params.get("aud");
        const origin = params.get("origin") || "/";
        const state = {
          mfaToken: newData.mfaToken,
          email: variables.email, // display only (masked) — never used for auth
          remember: variables.remember,
          isNavigatingInsideApplication: true,
        };
        if (!callback) {
          navigate("/mfa-totp", { state });
        } else {
          navigate(
            `/mfa-totp?callback=${encodeURIComponent(callback)}&aud=${encodeURIComponent(aud || "")}&origin=${encodeURIComponent(origin)}`,
            { replace: true, state },
          );
        }
        return;
      }

      // Admin-enforced mandatory setup — NO session yet. Handled here (before the SSO logic) so it
      // ALSO fires during a product SSO/callback login, not just the direct app login. feature/totp-mfa.
      if (newData.requiresTotpSetup) {
        const params = new URLSearchParams(window.location.search);
        const callback = params.get("callback");
        const aud = params.get("aud");
        const origin = params.get("origin") || "/";
        const state = { mfaToken: newData.mfaToken, remember: variables.remember };
        if (!callback) {
          navigate("/mfa-setup", { replace: true, state });
        } else {
          navigate(
            `/mfa-setup?callback=${encodeURIComponent(callback)}&aud=${encodeURIComponent(aud || "")}&origin=${encodeURIComponent(origin)}`,
            { replace: true, state },
          );
        }
        return;
      }

      queryClient.setQueryData([endpoint], newData);

      const params = new URLSearchParams(location.search);
      const callback = params.get("callback");
      const aud = params.get("aud");
      const origin = params.get("origin");

      if (!callback) {
        setAuthState(AuthState.Authenticated);
        navigate("/home", { state: { from: location }, replace: true });
        return;
      }

      if (aud) {
        // SSO flow: mint a product-scoped HS256 JWT for the requesting product.
        try {
          const ssoData = await post<{ productCode: string }, siginResponseModel>(
            `${endpoint}/sso`,
            { productCode: aud },
            false,
          );
          window.location.href = `${callback}?token=${encodeURIComponent(ssoData.accessToken)}`;
        } catch (error: any) {
          const rawMsg = error?.response?.data;
          const msg = typeof rawMsg === "string" ? rawMsg : (rawMsg?.message ?? "");
          const isNoAccess = msg.toLowerCase().includes("do not have access");
          window.location.href = `${callback}?error=${isNoAccess ? "no_product_access" : "sso_failed"}`;
        }
        return;
      }

      // Regular callback (non-SSO, no aud param — other integrations).
      window.location.href = `${callback}?tokenType=${newData.tokenType}&accessToken=${newData.accessToken}&expiresIn=${newData.expiresIn}&refreshToken=${newData.refreshToken}&expirationDate=${newData.expirationDate}&rememberMe=${rememberMe}&origin=${origin}`;
    },
    onError: (error: any) => {
      if (error?.response?.data) {
        const errorMessage =
          typeof error.response.data === "string"
            ? error.response.data
            : error.response.data?.message || "An unexpected error occurred."; // Object message handling

        onError(errorMessage);

        enqueueSnackbar(errorMessage, { variant: "error" });
      } else {
        onError("An unexpected error occurred.");
      }
    },
  });
};

// export const useAuth = () => {
//   const location = useLocation();
//   const authenticated = isAuthenticated();
//   const navigate = useNavigate();
//   const params = new URLSearchParams(location.search);
//   const callback = params.get('callback');
//   const origin = params.get('origin');
//   const aud = params.get('aud');

//   useEffect(() => {

//     if (callback) {
//       navigate(`/login?callback=${callback}&aud=${aud}&origin=${origin}`, { replace: true })
//     } else if (authenticated) {
//       navigate("/home", { replace: true });
//     }
//   }, [authenticated, callback, navigate, origin]);

//   return isAuthenticated;
// };

export const useLogout = () => {
  const navigate = useNavigate();
  const mainStore = useMainStore();
  const authStore = useAuthStore();
  const handlers = new Handlers(mainStore);
  const { post } = usePost();
  const { setLogoutModal: setModal } = useHomeStore();

  return useMutation<string, Error>({
    mutationFn: () => {
      return post<undefined, string>(`${endpoint}/logout`, undefined, false);
    },
    onSuccess: () => {
      //removeAuthorizationHeader();
      setModal(false);
      mainStore.setCurrentUser(undefined);
      authStore.setPrivilege([]);
      handlers.handleSelectClient(null as any, null as any);
      localStorage.clear();

      sessionStorage.setItem("skipSessionCheck", "1");

      navigate("/login", {
        replace: true,
      });
    },
    onError: () => {
      // Handle error
    },
  });
};

//Forgot Password Form Page
interface UseForgotPasswordProps {
  onSuccess: (data: ForgotPasswordResponse) => void;
  onError: (error: any) => void;
}

export const useForgotPassword = ({
  onSuccess,
  onError,
}: UseForgotPasswordProps) => {
  const { post } = usePost();

  return useMutation<ForgotPasswordResponse, Error, ForgotPasswordCommand>({
    mutationFn: (data: ForgotPasswordCommand) => {
      try {
        return post<ForgotPasswordCommand, ForgotPasswordResponse>(
          `${endpoint}/forgot-password-link`,
          data,
          false,
        );
      } catch (error: any) {
        if (error?.response?.data) {
          throw new Error(error.response.data);
        }
        throw new Error("An unexpected error occurred");
      }
    },
    onSuccess: (data: ForgotPasswordResponse) => {
      onSuccess(data);
      enqueueSnackbar(
        "Success! Please check your email for the password reset link.",
        { variant: "success" },
      );
    },
    onError: (error: any) => {
      console.error("Axios Error:", error);
      if (error?.response?.data) {
        const errorMessage =
          typeof error.response.data === "string"
            ? error.response.data
            : error.response.data?.message || "An unexpected error occurred.";
        onError(errorMessage);
      } else {
        onError("An unexpected error occurred.");
      }
    },
  });
};

//Reset Password Link Form Page
interface UseResetPasswordProps {
  onSuccess: (data: ResetPasswordResponse) => void;
  onError: (error: any) => void;
}

export const useResetPassword = ({
  onSuccess,
  onError,
}: UseResetPasswordProps) => {
  const { post } = usePost();

  return useMutation<ResetPasswordResponse, Error, ResetPasswordCommand>({
    mutationFn: (data: ResetPasswordCommand) => {
      return post<ResetPasswordCommand, ResetPasswordResponse>(
        `${endpoint}/reset-password-link`,
        data,
      );
    },
    onSuccess,
    onError,
  });
};

export const useVerifyTwoFactorCode = ({
  onError,
}: {
  onError: (error: any) => void;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { post } = usePost();
  const { rememberMe } = useSignInStore();

  return useMutation<siginResponseModel, Error, VerifyTwoFactorModel>({
    mutationFn: (data: VerifyTwoFactorModel) => {
      try {
        const normalizedCode = data.code ?? data.otp ?? "";
        const payload = {
          ...data,
          code: normalizedCode,
          otp: normalizedCode,
        };
        //removeAuthorizationHeader();
        return post<VerifyTwoFactorModel, siginResponseModel>(
          `${endpoint}/verify-otp`,
          payload,
          false,
        );
      } catch (err: any) {
        if (err?.response?.data) {
          throw new Error(err.response.data);
        }
        throw new Error("An unexpected error occurred");
      }
    },
    onSuccess: (newData, variables) => {
      queryClient.setQueryData([endpoint, "verify-otp"], newData);

      const params = new URLSearchParams(location.search);
      const callback = params.get("callback");
      const origin = params.get("origin");
      const aud = params.get("aud");

      if (!callback) {
        // setAuthData(newData);
        // setAuthorizationHeader(newData.accessToken);
        navigate("/home", { state: { from: location }, replace: true });
      } else {
        window.location.href =
          `${callback}?tokenType=${newData.tokenType}` +
          // `&accessToken=${newData.accessToken}` +
          `&expiresIn=${newData.expiresIn}` +
          //`&refreshToken=${newData.refreshToken}` +
          `&expirationDate=${newData.expirationDate}` +
          `&rememberMe=${rememberMe}` +
          `&origin=${origin}` +
          `&aud=${aud}`;
      }
    },
    onError: (err: any) => {
      if (err?.response?.data) {
        const msg =
          typeof err.response.data === "string"
            ? err.response.data
            : err.response.data?.message || "An unexpected error occurred.";
        if (
          msg ===
          "You’ve entered an invalid code too many times. Your account has been temporarily locked for 10 minutes. Please try again later."
        ) {
          onError(msg);
          const params = new URLSearchParams(location.search);
          const callback = params.get("callback");
          const origin = params.get("origin");
          const aud = params.get("aud");
          if (!callback) {
            setTimeout(() => {
              navigate("/login");
            }, 3000);
          } else {
            setTimeout(() => {
              navigate(
                `/login?callback=${callback}&aud=${aud}&origin=${origin}`,
                { replace: true },
              );
            }, 3000);
          }
        }
        onError(msg);
      } else {
        onError("An unexpected error occurred.");
      }
    },
  });
};

// Authenticator (TOTP) login step-up verify — feature/totp-mfa
export const useVerifyTotp = ({
  onError,
}: {
  onError: (error: any) => void;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { post } = usePost();
  const { setAuthState } = useAuthStore();
  const { rememberMe } = useSignInStore();

  return useMutation<siginResponseModel, Error, VerifyTotpModel>({
    mutationFn: (data: VerifyTotpModel) => {
      try {
        return post<VerifyTotpModel, siginResponseModel>(
          `${endpoint}/mfa/authenticator/verify`,
          data,
          false,
        );
      } catch (err: any) {
        if (err?.response?.data) {
          throw new Error(err.response.data);
        }
        throw new Error("An unexpected error occurred");
      }
    },
    onSuccess: (newData) => {
      queryClient.setQueryData([endpoint, "verify-totp"], newData);

      const params = new URLSearchParams(location.search);
      const callback = params.get("callback");
      const origin = params.get("origin");
      const aud = params.get("aud");

      if (!callback) {
        setAuthState(AuthState.Authenticated);
        navigate("/home", { state: { from: location }, replace: true });
      } else {
        window.location.href =
          `${callback}?tokenType=${newData.tokenType}` +
          `&expiresIn=${newData.expiresIn}` +
          `&expirationDate=${newData.expirationDate}` +
          `&rememberMe=${rememberMe}` +
          `&origin=${origin}` +
          `&aud=${aud}`;
      }
    },
    onError: (err: any) => {
      const msg =
        typeof err?.response?.data === "string"
          ? err.response.data
          : err?.response?.data?.message ||
            err?.message ||
            "An unexpected error occurred.";
      onError(msg);
      enqueueSnackbar(msg, { variant: "error" });
    },
  });
};

// Mandatory authenticator setup (admin-required) — token-authed, no session yet. feature/totp-mfa
export const useMfaSetupStart = () => {
  const { post } = usePost();
  return useMutation<MfaSetupStartResult, Error, { mfaToken: string }>({
    mutationFn: (data) =>
      post(`${endpoint}/mfa/authenticator/setup/start`, data, false),
  });
};

export const useMfaSetupVerify = () => {
  const { post } = usePost();
  const { setAuthState } = useAuthStore();
  return useMutation<MfaSetupResult, Error, MfaSetupVerifyModel>({
    mutationFn: (data) =>
      post(`${endpoint}/mfa/authenticator/setup/verify`, data, false),
    onSuccess: () => {
      // The session cookie is now set by the server — the user is authenticated.
      setAuthState(AuthState.Authenticated);
    },
  });
};

interface BackendResponse {
  data: string;
  message: string;
}

interface ResendTwoFactorCode {
  email: string;
  checkLockoutOnly: boolean;
}

interface UseResendTwoFactorCodeProps {
  onSuccess: (payload: string) => void;
  onError: (error: string) => void;
}

export const useResendTwoFactorCodeCode = ({
  onSuccess,
  onError,
}: UseResendTwoFactorCodeProps) => {
  const { post } = usePost();

  return useMutation<BackendResponse, Error, ResendTwoFactorCode>({
    mutationFn: async (payload: ResendTwoFactorCode) => {
      try {
        const response = await post<ResendTwoFactorCode, BackendResponse>(
          `${endpoint}/resend-otp`,
          payload,
          false,
        );
        return response;
      } catch (error: any) {
        const msg = error?.response?.data;
        if (typeof msg === "string") {
          throw new Error(msg);
        }
        throw new Error("An unexpected error occurred");
      }
    },

    onSuccess: (response) => {
      const payloadString = response.data;

      if (payloadString.includes("minute(s)")) {
        enqueueSnackbar("OTP sent successfully", { variant: "success" });
      }

      onSuccess(payloadString);
    },

    onError: (error: any) => {
      console.error("Axios Error:", error);
      if (error?.response?.data) {
        const errorMessage =
          typeof error.response.data === "string"
            ? error.response.data
            : error.response.data?.message || "An unexpected error occurred.";
        onError(errorMessage);
      } else {
        onError("An unexpected error occurred.");
      }
    },
  });
};
